import {
    CdkCell,
    CdkCellDef,
    CdkColumnDef,
    CdkHeaderCell,
    CdkHeaderCellDef,
    CdkHeaderRow,
    CdkHeaderRowDef,
    CdkRow,
    CdkRowDef,
    CdkTable,
} from '@angular/cdk/table';
import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    ContentChild,
    ContentChildren,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    Optional,
    Output,
    QueryList,
    TemplateRef,
    TrackByFunction,
    ViewChild,
    ViewChildren,
} from '@angular/core';

import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { SewButton, SewCheckbox, SewTable } from '@sew/ui-components-angular/standalone';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { WINDOW } from '../../shared/window-token';
import {
    isCategoryHeaderRow,
    isExpandableRow,
    isFullRowContent,
    isGroupHeaderRow,
    isSelectableRow,
} from './base-table.helper';
import {
    ExpandableRow,
    GroupHeaderRow,
    GroupWithContentRows,
    InputRow,
    SelectableRow,
} from './base-table.interface';
import { TableScrollTargetDirective } from './directives/table-scroll-target/table-scroll-target.directive';
import { TableFilterPipe } from './pipes/table-filter.pipe';
import { TableSortPipe } from './pipes/table-sort.pipe';
import { InputRowFilter } from './services/table-filter/table-filter.interface';
import { TableFilterService } from './services/table-filter/table-filter.service';
import { InputRowSorter } from './services/table-sort/table-sort.interface';
import { TableSortService } from './services/table-sort/table-sort.service';

@Component({
    selector: 'osc-base-table',
    templateUrl: './base-table.component.html',
    styleUrls: ['./base-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        CdkTable,
        CdkDropList,
        CdkColumnDef,
        CdkHeaderCellDef,
        CdkHeaderCell,
        CdkCellDef,
        CdkCell,
        CdkHeaderRowDef,
        CdkHeaderRow,
        CdkRowDef,
        CdkRow,
        CdkDrag,
        TableScrollTargetDirective,
        NgTemplateOutlet,
        AsyncPipe,
        TableFilterPipe,
        TableSortPipe,
        SewButton,
        SewCheckbox,
        SewTable,
    ],
})
export class BaseTableComponent<T, G, E> implements AfterContentInit, OnDestroy {
    @Input() public columns: string[] | null = null;
    @Input() public groupHeaderColumns: string[] | null = null;
    @Input() public checkRowsNeeded = false;
    /**
     * If true, the blue bar indicator of the marked row is invisible
     */
    @Input() public hideMarkedBar = false;
    @Input() public showCheckboxColumn = true;
    @Input() public showExpandToggle = false;
    @Input() public showEditColumn = false;
    @Input() public showScrollbarAtTop = false;
    @Input() public compareOnlyRef = true;
    @Input() public compareFunction: (row: InputRow<T, G, E>, checkedRows: SelectableRow<T>[]) => boolean;

    /**
     * If group and category rows are needed, then `getUniqueGroupIdFunction` must be supplied, in order
     * to get the group or category row on it's contained rows in the template.
     *
     * `getUniqueGroupIdFunction` is called in the setter of the property `items` and must therefore be
     * supplied before the `items`-input in the template of the parent component:
     *
     * **Correct**:
     * ```html
     * <osc-base-table
     *  [getUniqueGroupIdFunction]="getUniqueGroupIdFunction"
     *  [items]="partGroups"
     *  ...
     * />
     * ```
     * **False**:
     * ```html
     * <osc-base-table
     *  [items]="partGroups"
     *  [getUniqueGroupIdFunction]="getUniqueGroupIdFunction"
     *  ...
     * />
     * ```
     */
    @Input() public getUniqueGroupIdFunction: (row: G) => string;
    @ViewChild(CdkTable, { static: true }) public table!: CdkTable<InputRow<T, G, E>>;
    @ContentChildren(CdkColumnDef) public columnDefs!: QueryList<CdkColumnDef>;

    /**
     * Rows displayed/rendered by the CDK table
     */
    @ViewChildren(TableScrollTargetDirective)
    public displayRows!: QueryList<TableScrollTargetDirective>;

    @ContentChild('actionBar') public actionBar?: TemplateRef<any>;

    @Output() public readonly rowSelect = new EventEmitter<SelectableRow<T>[]>();
    @Output() public readonly rowMarked = new EventEmitter<SelectableRow<T> | null>();
    @Output() public readonly requestRows = new EventEmitter<void>();
    @Output() public readonly editRow = new EventEmitter<InputRow<T, G, E>>();

    @Output() public readonly dropEvent = new EventEmitter<
        CdkDragDrop<readonly InputRow<T, G, E>[], any, any>
    >();

    /**
     * Input rows for the CDK table
     */
    public displayItems: readonly InputRow<T, G, E>[] = [];
    public groupItems: {
        [key: string]: GroupHeaderRow<G, T> | GroupWithContentRows<G, T, E>;
    } = {};

    public selectedRow: InputRow<T, G, E> | null = null;
    public expandedRow: InputRow<T, G, E> | null = null;
    public expandedChildRow: InputRow<T, G, E> | null = null;
    public checkedRows: SelectableRow<T>[] = [];

    public readonly filters: Observable<InputRowFilter<T, G, E>[]> | null =
        this.filterService?.filters.pipe(map((filters) => filters.map(({ rowFilter }) => rowFilter))) ?? null;

    public readonly sorter: Observable<InputRowSorter<T, G, E> | undefined> | null =
        this.sortService?.sorter.pipe(map((sorter) => sorter?.rowSorter)) ?? null;

    private headerThemeFrameId: number | null = null;

    /**
     * If group and category rows are passed, then `getUniqueGroupIdFunction` must be supplied, in order
     * to get the group or category row on it's contained rows in the template.
     *
     * `getUniqueGroupIdFunction` is called in the setter of the property `items` and must therefore be
     * supplied before the `items`-input in the template of the parent component:
     *
     * **Correct**:
     * ```html
     * <osc-base-table
     *  [getUniqueGroupIdFunction]="getUniqueGroupIdFunction"
     *  [items]="partGroups"
     *  ...
     * />
     * ```
     * **False**:
     * ```html
     * <osc-base-table
     *  [items]="partGroups"
     *  [getUniqueGroupIdFunction]="getUniqueGroupIdFunction"
     *  ...
     * />
     * ```
     */
    @Input() public set items(items: readonly InputRow<T, G, E>[] | null) {
        if (!items) {
            this.displayItems = [];

            return;
        }

        this.displayItems = items.reduce(
            (display, row, _, arr) => {
                if (isGroupHeaderRow(row)) {
                    return [...display, ...(arr.length > 1 ? [row] : []), ...row.containedRows];
                }

                return [...display, row];
            },
            [] as InputRow<T, G, E>[]
        );

        this.groupItems = this.displayItems.reduce(
            (group, item) => {
                if (isGroupHeaderRow(item)) {
                    group[this.getUniqueGroupIdFunction(item.rowData)] = item;
                }

                return group;
            },
            {} as { [key: string]: GroupHeaderRow<G, T> | GroupWithContentRows<G, T, E> }
        );
    }

    @Input() public set markedRow(markedRow: InputRow<T, G, E> | null) {
        this.selectedRow = markedRow;

        if (markedRow) {
            this.scrollToRow(markedRow);
        }
    }

    @Input() public set checked(checkedRows: InputRow<T, G, E>[] | undefined) {
        this.checkedRows = checkedRows?.filter(isSelectableRow) ?? [];
    }

    public constructor(
        @Inject(WINDOW) private window: Window,
        @Optional() private filterService: TableFilterService<T, G, E> | null,
        @Optional() private sortService: TableSortService<T, G, E> | null
    ) {
        this.compareFunction = () => false;
        this.getUniqueGroupIdFunction = () => '';
    }

    @Input() public trackBy: TrackByFunction<InputRow<T, G, E>> = (_, item) => item;

    @Input() dragEnabled: boolean = false;

    public get selectableRows(): SelectableRow<T>[] {
        return this.displayItems
            .filter<SelectableRow<T>>(isSelectableRow)
            .filter((row) => !row.isDisabled && !this.isFilteredRow(row));
    }

    public get displayColumns(): string[] {
        let columns = [...(this.columns ?? [])];

        columns = this.showCheckboxColumn ? ['checkbox', ...columns] : columns;
        columns = this.showExpandToggle ? ['expand-toggle', ...columns] : columns;
        columns = this.showEditColumn ? [...columns, 'edit'] : columns;

        return columns;
    }

    public get displayGroupHeaderColumns(): string[] {
        let columns = [...(this.groupHeaderColumns ?? [])];

        columns = this.showCheckboxColumn ? ['checkbox', ...columns] : columns;
        columns = this.showExpandToggle ? ['expand-toggle', ...columns] : columns;

        return columns;
    }

    public get categoryHeaderColumns(): string[] {
        return ['collapse', 'groupHeaderName'];
    }

    public isGroupHeaderRow = (_index: number, row: any) =>
        isGroupHeaderRow(row) && !isCategoryHeaderRow(row);

    public isCategoryHeaderRow = (_index: number, row: any) => isCategoryHeaderRow(row);

    public isExpandableRow = (_index: number, row: any) => isExpandableRow(row);

    public showDefaultRow = (_index: number, row: any) => !isGroupHeaderRow(row);

    public isSelectableRow = (row: any) => isSelectableRow(row);

    public isFullRowContent = (_index: number, row: any) => isFullRowContent(row);

    public isFilteredRow = (row: any) => (row as any).isFiltered;

    public ngAfterContentInit() {
        this.columnDefs.forEach((columnDef) => this.table.addColumnDef(columnDef));
    }

    public ngOnDestroy() {
        if (this.headerThemeFrameId !== null) {
            this.window.cancelAnimationFrame?.(this.headerThemeFrameId);
            this.headerThemeFrameId = null;
        }
    }

    public applyTableHeaderTheme() {
        const tableHeader = this.getTableHeader();

        if (tableHeader) {
            tableHeader.classList.add('sew-theme-dark');

            return;
        }

        if (this.headerThemeFrameId !== null) {
            return;
        }

        const scheduledFrameId = this.window.requestAnimationFrame?.(() => {
            this.headerThemeFrameId = null;
            this.getTableHeader()?.classList.add('sew-theme-dark');
        });

        this.headerThemeFrameId = typeof scheduledFrameId === 'number' ? scheduledFrameId : null;
    }

    private getTableHeader(): HTMLTableSectionElement | null {
        const nativeElement = (this.table as any)?._elementRef?.nativeElement as HTMLElement | undefined;
        const tableElement =
            nativeElement instanceof HTMLTableElement ? nativeElement : nativeElement?.querySelector('table');

        return (tableElement?.tHead ??
            tableElement?.querySelector('thead')) as HTMLTableSectionElement | null;
    }

    public toggleExpand(row: InputRow<T, G, E>) {
        this.expandedRow = this.expandedRow === row ? null : row;
    }

    public editRowClicked(row: InputRow<T, G, E>) {
        this.editRow.emit(row);
    }

    public isCheckAllChecked() {
        const checkedSelectableRows = this.getCheckedSelectableRows();

        return checkedSelectableRows.length && checkedSelectableRows.length === this.selectableRows.length;
    }

    public isCheckAllDisabled() {
        const checkedSelectableRows = this.getCheckedSelectableRows();

        return !checkedSelectableRows.length && checkedSelectableRows.length === this.selectableRows.length;
    }

    public isCheckAllIndeterminate(): boolean {
        const checkedSelectableRows = this.getCheckedSelectableRows();

        return (
            this.selectableRows.length > 0 &&
            checkedSelectableRows.length > 0 &&
            checkedSelectableRows.length < this.selectableRows.length
        );
    }

    public isRowSelectionDisabled(row: InputRow<T, G, E>): boolean {
        if (isGroupHeaderRow(row)) {
            return row.isDisabled || !this.getSelectableGroupRows(row).length;
        }

        return isSelectableRow(row) && row.isDisabled;
    }

    public isRowChecked(row: InputRow<T, G, E>): boolean {
        if (isGroupHeaderRow(row)) {
            const selectableGroupRows = this.getSelectableGroupRows(row);

            return (
                selectableGroupRows.length > 0 &&
                selectableGroupRows.every((groupRow) => this.isRowChecked(groupRow))
            );
        }

        return isSelectableRow(row) && this.getCheckedRowIndex(row) !== -1;
    }

    public isGroupHeaderPartiallySelected(row: InputRow<T, G, E>): boolean {
        if (!isGroupHeaderRow(row)) {
            return false;
        }

        const selectableGroupRows = this.getSelectableGroupRows(row);
        const selectedGroupRows = selectableGroupRows.filter((groupRow) => this.isRowChecked(groupRow));

        return selectedGroupRows.length > 0 && selectedGroupRows.length < selectableGroupRows.length;
    }

    public unselectAllChecked() {
        this.updateCheckedRows([]);
    }

    public isValidExpandableRow(row: any): row is ExpandableRow<T, E> {
        return 'expandData' in row && 'isExpandableRow' in row;
    }

    public toggleAllChecked(checked: boolean) {
        const selectableRows = this.selectableRows;
        const nonVisibleRows = this.checkedRows.filter(
            (checkedRow) => !selectableRows.some((selectableRow) => this.rowsMatch(selectableRow, checkedRow))
        );

        this.updateCheckedRows(checked ? [...nonVisibleRows, ...selectableRows] : nonVisibleRows);
    }

    public toggleRowChecked(row: InputRow<T, G, E>, checked: boolean, updateRowMark: boolean = true): void {
        if (isGroupHeaderRow(row)) {
            this.toggleGroupRowsChecked(row, checked);

            return;
        }

        if (!isSelectableRow(row) || row.isDisabled) {
            return;
        }

        const checkedIndex = this.getCheckedRowIndex(row);

        if (!checked && checkedIndex !== -1) {
            this.updateCheckedRows([
                ...this.checkedRows.slice(0, checkedIndex),
                ...this.checkedRows.slice(checkedIndex + 1),
            ]);

            if (this.selectedRow === row) {
                if (updateRowMark) {
                    this.unmarkRow();
                }
            }
        } else if (checkedIndex === -1) {
            this.updateCheckedRows([...this.checkedRows, row]);
            if (updateRowMark) {
                this.markRow(row);
            }
        }
    }

    public toggleRowMarked(row: InputRow<T, G, E>): void {
        if (!isExpandableRow(row) && !isGroupHeaderRow(row)) {
            return;
        }

        if (this.selectedRow === row) {
            this.unmarkRow();
        } else {
            this.markRow(row);
        }
    }

    public onRequestRows() {
        this.requestRows.emit();
    }

    public drop($event: CdkDragDrop<readonly InputRow<T, G, E>[], any, any>) {
        this.dropEvent.emit($event);
    }

    private updateCheckedRows(rows: SelectableRow<T>[]): void {
        const items = this.displayItems;
        this.checkedRows = rows.sort(
            (a, b) => items.findIndex((item) => item === a) - items.findIndex((item) => item === b)
        );

        this.rowSelect.emit(this.checkedRows);
    }

    private getCheckedRowIndex(row: InputRow<T, G, E>): number {
        return this.checkedRows.findIndex((checkedRow) => this.rowsMatch(row, checkedRow));
    }

    private getCheckedSelectableRows(): SelectableRow<T>[] {
        return this.checkedRows.filter((checkedRow) =>
            this.selectableRows.some((selectableRow) => this.rowsMatch(selectableRow, checkedRow))
        );
    }

    private getSelectableGroupRows(
        groupHeader: GroupHeaderRow<G, T> | GroupWithContentRows<G, T, E>
    ): SelectableRow<T>[] {
        return groupHeader.containedRows.reduce<SelectableRow<T>[]>((selectableRows, containedRow) => {
            if (
                isSelectableRow(containedRow) &&
                !containedRow.isDisabled &&
                !this.isFilteredRow(containedRow)
            ) {
                selectableRows.push(containedRow);
            }

            return selectableRows;
        }, []);
    }

    private toggleGroupRowsChecked(
        groupHeader: GroupHeaderRow<G, T> | GroupWithContentRows<G, T, E>,
        checked: boolean
    ): void {
        const selectableGroupRows = this.getSelectableGroupRows(groupHeader);
        const rowsOutsideGroup = this.checkedRows.filter(
            (checkedRow) =>
                !this.rowsMatch(groupHeader, checkedRow) &&
                !selectableGroupRows.some((groupRow) => this.rowsMatch(groupRow, checkedRow))
        );

        this.updateCheckedRows(checked ? [...rowsOutsideGroup, ...selectableGroupRows] : rowsOutsideGroup);
    }

    private rowsMatch(row: InputRow<T, G, E>, checkedRow: SelectableRow<T>): boolean {
        return this.compareOnlyRef ? row === checkedRow : this.compareFunction(row, [checkedRow]);
    }

    private unmarkRow() {
        this.selectedRow = null;
        this.rowMarked.emit(null);
    }

    private markRow(row: InputRow<T, G, E>) {
        if (!isSelectableRow(row)) {
            return;
        }

        this.selectedRow = row;
        this.rowMarked.emit(row);
    }

    private scrollToRow(row: InputRow<T, G, E>) {
        const scrollTarget = this.displayRows.find((displayRow) => displayRow.scrollToValue === row);

        if (!scrollTarget) {
            return;
        }

        const element = scrollTarget.elementRef.nativeElement as HTMLElement;
        const rect = element.getBoundingClientRect();

        // using "start"/"center" for block might result in the target
        // being hidden by sticky elements such as the action bar
        if (rect.bottom > this.window.innerHeight || rect.top < 0) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }
}
